import React, {useState} from "react";
import {useLocation, useNavigate} from "react-router-dom";

import {phoneValidate, sendSMS, verifyCode, verifyRegisterCode} from "../../services/authService";
import {useToken} from "../../context/TokenContext";
import {useBooking} from "../../context/BookingContext";
import {CountryCode} from "../../utils/Constants";
import {SectionHeading} from "../SectionHeading";
import {PhoneInput} from "./phoneNumberInput";
import {VerificationCodeInput} from "./verificationCodeInput";
import {useTranslation} from "react-i18next";
import i18n from "../../i18n";

const AuthLinkSection: React.FC<{
  isLogin: boolean;
  toggleMode: () => void;
}> = ({ isLogin, toggleMode }) => {
  const { t } = useTranslation();
  return (
  <p className="text-center mt-4 text-sm">
    {isLogin ? (
      <>
      {t('AuthDontHaveAccount')} {" "}
        <button onClick={toggleMode} className="text-yellow-400 hover:underline font-bold">
          {t('Signup')}
        </button>
      </>

    ) : (
      <>
      {t('AuthAlreadyHaveAccount')} {" "}
        <button onClick={toggleMode} className="text-yellow-400 hover:underline font-bold">
          {t('Login')}
        </button>
      </>
    )}
  </p> )
};

interface AuthFormProps {
  bookingMode?: boolean;
  onSuccessAuth?: (success: boolean) => void;
}
const AuthForm: React.FC<AuthFormProps> = ({
  bookingMode = false,
  onSuccessAuth = () => {}
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { state } = location;
  const from = state?.from || '/'; // Default to home if no redirect path was specified
  const { setTokens, setRegisterToken } = useToken();
  const { setBookingUserInfo } = useBooking();

  const [isLogin, setIsLogin] = useState(true);     // True for login, false for register
  const toggleMode = () => setIsLogin((prev) => !prev);
  const title = isLogin ? t('AuthLogin') : t('AuthSignup');

  // States
  const [phoneNumber, setPhoneNumber] = useState("");
  const [countryCode, setCountryCode] = useState(CountryCode.HONG_KONG);
  const [isPhoneValid, setIsPhoneValid] = useState(true);
  const [smsCode, setSMSCode] = useState("");

  const [errorMessage, setErrorMessage] = useState("");
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [buttonText, setButtonText] = useState(t("AuthSendOTP"));

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Handle phone number validation
  const handlePhoneValidation = async () => {
    const isValid = phoneValidate({ phoneNumber, countryCode });

    // Map i18n language to API language code
    const getLanguageCode = (): number => {
      switch (i18n.language) {
        case "en": return 0; // English
        case "zh": return 1; // Traditional Chinese
        case "cn": return 2; // Simplified Chinese
        default: return 0;   // Default to English
      }
    };

    const languageCode = getLanguageCode();

    if (isValid) {
      setIsPhoneValid(true);
      setErrorMessage("");
      setIsButtonDisabled(true);

      try {
        // Await the sendSMS function to handle the returned Promise
        const success = await sendSMS({ countryCode, phoneNumber, languageCode });
        if (!success) {
          setErrorMessage("Failed to send OTP. Please try again.");
          setIsButtonDisabled(false);
          return false;
        }
      } catch (error) {
        console.error("Failed to send OTP:", error);
        setErrorMessage("Failed to send OTP. Please try again.");
      }

      return true;
    } else {
      setIsPhoneValid(false);
      setErrorMessage(t('AuthPhoneNumberError'));
      return false;
    }
  };

  const handleVerifyCode = async () => {
    // if (isSubmitting) return;
    try {
      setIsSubmitting(true);
      if (!isLogin) {
        // Handle register case
        const result = await verifyRegisterCode({countryCode, phoneNumber, smsCode});
        setRegisterToken(result.registerToken);
        navigate('/register', {
          state: {
            phoneNumber,
            countryCode
          }
        });
      } else {
        // Handle login case
        const result = await verifyCode({ countryCode, phoneNumber, smsCode});
        const { nLoginToken, nRefreshToken, serviceToken, loginExpireAt, refreshExpireAt } = result;
        setTokens(nLoginToken, nRefreshToken, serviceToken, loginExpireAt, refreshExpireAt);
        setBookingUserInfo(phoneNumber, countryCode);
        if (!bookingMode) {
          navigate(from, {
            replace: true,
            state: {
              ...(location.state || {}),
              phoneNumber,
              toastMessage: 'Login successful',
              toastMode: 'success',
            }
          });
        } else {
          onSuccessAuth(true);
        }
      }
    } catch (error) {
      console.error("Verification failed", error);
    } finally {
      setTimeout(() => {
        setIsSubmitting(false);
      }, 1000);
    }
  };

  return (
    <div className="flex items-center justify-center my-10">
      <div className={`w-full ${!bookingMode ? 'md:w-[45%] ' : 'shadow-lg bg-gray-900'} p-8 rounded-lg`}>
        {/* Section Heading */}
        <SectionHeading size="lg">{title}</SectionHeading>

        {/* Phone Number Input Field */}
        <PhoneInput
          phoneNumber={phoneNumber}
          setPhoneNumber={setPhoneNumber}
          countryCode={countryCode}
          setCountryCode={setCountryCode}
          isPhoneValid={isPhoneValid}
          errorMessage={errorMessage}
        />

        {/* Verification Code Input */}
        <VerificationCodeInput
          handleValidation={handlePhoneValidation}
          isButtonDisabled={isButtonDisabled}
          buttonText={buttonText}
          smsCode={smsCode}
          setSMSCode={setSMSCode}
          setButtonText={setButtonText}
          setIsButtonDisabled={setIsButtonDisabled}
        />

        {/* Login button / Register button */}
        <button
          className={`w-full py-3 ${
            isSubmitting || !(phoneValidate({ phoneNumber, countryCode }) && smsCode.length === 4)
              ? "bg-gray-500 cursor-not-allowed"
              : "bg-orange-500 hover:bg-orange-600"
          } text-white font-bold rounded-lg transition-colors`}
          onClick={handleVerifyCode}
          disabled={isSubmitting || smsCode.length !== 4}
        >
          {isSubmitting ? (
            <span className="flex items-center justify-center">
            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            {t('AuthProcessing')}
          </span>
          ) : (
            title
          )}
        </button>

        {/* Login or Register */}
        <AuthLinkSection isLogin={isLogin} toggleMode={toggleMode} />
      </div>
    </div>
  );
};

export default AuthForm;